/**
 * TrendingScents - Shows trending scents in the area with real-time updates
 */

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocialStore } from '@/stores/useSocialStore';
import { cn } from '@/utils/cn';

interface TrendingScentData {
  scent: string;
  count: number;
  trend: 'rising' | 'stable' | 'falling';
  emoji: string;
  color: string;
}

// Scent metadata
const SCENT_META: Record<string, { emoji: string; color: string }> = {
  floral: { emoji: '🌸', color: '#f472b6' },
  pine: { emoji: '🌲', color: '#22c55e' },
  ocean: { emoji: '🌊', color: '#38bdf8' },
  coffee: { emoji: '☕', color: '#a16207' },
  rain: { emoji: '🌧️', color: '#6366f1' },
  grass: { emoji: '🍃', color: '#84cc16' },
  bread: { emoji: '🍞', color: '#f59e0b' },
  jasmine: { emoji: '🌺', color: '#e879f9' },
  beer: { emoji: '🍺', color: '#fbbf24' },
  woods: { emoji: '🌳', color: '#65a30d' },
  herbs: { emoji: '🌿', color: '#4ade80' },
  smoke: { emoji: '🔥', color: '#64748b' },
  industrial: { emoji: '🏭', color: '#ef4444' },
  petrichor: { emoji: '💧', color: '#06b6d4' },
  food: { emoji: '🍜', color: '#fbbf24' },
  sewage: { emoji: '⚠️', color: '#dc2626' },
  perfume: { emoji: '✨', color: '#c084fc' },
};

interface TrendingScentsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TrendingScents({ isOpen, onClose }: TrendingScentsProps) {
  const feed = useSocialStore((state) => state.feed);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d'>('24h');

  // Calculate trending scents from feed
  const trendingScents = useMemo(() => {
    const now = new Date().getTime();
    const ranges = {
      '1h': 3600000,
      '24h': 86400000,
      '7d': 604800000,
    };

    // Filter reports by time range
    const recentReports = feed.filter(
      (report) => now - new Date(report.created_at).getTime() < ranges[timeRange]
    );

    // Count scent mentions
    const scentCounts: Record<string, number> = {};
    recentReports.forEach((report) => {
      if (!report.scent_notes) return;
      const lower = report.scent_notes.toLowerCase();

      Object.keys(SCENT_META).forEach((scent) => {
        if (lower.includes(scent)) {
          scentCounts[scent] = (scentCounts[scent] || 0) + 1;
        }
      });

      // Also extract custom scents
      const words = lower.split(/[,\s]+/);
      words.forEach((word) => {
        if (word.length > 3 && !Object.keys(SCENT_META).includes(word)) {
          scentCounts[word] = (scentCounts[word] || 0) + 1;
        }
      });
    });

    // Convert to array and sort
    const trending: TrendingScentData[] = Object.entries(scentCounts)
      .map(([scent, count]) => ({
        scent,
        count,
        trend: (count > 3 ? 'rising' : count > 1 ? 'stable' : 'falling') as 'rising' | 'stable' | 'falling',
        emoji: SCENT_META[scent]?.emoji || '☁️',
        color: SCENT_META[scent]?.color || '#00ffd0',
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return trending;
  }, [feed, timeRange]);

  // Total reports in time range
  const totalReports = useMemo(() => {
    const now = new Date().getTime();
    const ranges = {
      '1h': 3600000,
      '24h': 86400000,
      '7d': 604800000,
    };
    return feed.filter(
      (report) => now - new Date(report.created_at).getTime() < ranges[timeRange]
    ).length;
  }, [feed, timeRange]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-24 left-4 z-40 w-80"
        >
          <div className="bg-[#0a1520]/95 backdrop-blur-xl rounded-2xl border border-cyan-500/20 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-slate-700/50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📈</span>
                  <h3 className="font-bold text-white">Trending Scents</h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>

              {/* Time range selector */}
              <div className="flex gap-1">
                {(['1h', '24h', '7d'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={cn(
                      'px-3 py-1 rounded-full text-xs font-medium transition-all',
                      timeRange === range
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'text-slate-400 hover:text-white'
                    )}
                  >
                    {range === '1h' ? 'Last Hour' : range === '24h' ? '24 Hours' : '7 Days'}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats bar */}
            <div className="px-4 py-2 bg-slate-800/30 flex items-center justify-between text-xs">
              <span className="text-slate-400">
                {totalReports} report{totalReports !== 1 ? 's' : ''} in this period
              </span>
              <span className="text-emerald-400 flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                Live
              </span>
            </div>

            {/* Trending list */}
            <div className="p-4 space-y-2 max-h-[300px] overflow-y-auto">
              {trendingScents.length === 0 ? (
                <div className="text-center py-8">
                  <span className="text-4xl mb-2 block">☁️</span>
                  <p className="text-slate-400 text-sm">No scents reported yet</p>
                  <p className="text-slate-500 text-xs mt-1">Be the first to report!</p>
                </div>
              ) : (
                trendingScents.map((item, index) => (
                  <motion.div
                    key={item.scent}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    {/* Rank */}
                    <div className="w-6 h-6 rounded-full bg-slate-700/50 flex items-center justify-center">
                      <span className="text-xs font-bold text-slate-300">#{index + 1}</span>
                    </div>

                    {/* Scent icon */}
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${item.color}20` }}
                    >
                      <span className="text-lg">{item.emoji}</span>
                    </div>

                    {/* Scent name and count */}
                    <div className="flex-1">
                      <p className="text-white font-medium capitalize">{item.scent}</p>
                      <p className="text-xs text-slate-400">
                        {item.count} report{item.count !== 1 ? 's' : ''}
                      </p>
                    </div>

                    {/* Trend indicator */}
                    <div
                      className={cn(
                        'px-2 py-1 rounded-full text-[10px] font-medium',
                        item.trend === 'rising' && 'bg-emerald-500/20 text-emerald-400',
                        item.trend === 'stable' && 'bg-slate-500/20 text-slate-400',
                        item.trend === 'falling' && 'bg-red-500/20 text-red-400'
                      )}
                    >
                      {item.trend === 'rising' && '↑ Rising'}
                      {item.trend === 'stable' && '→ Stable'}
                      {item.trend === 'falling' && '↓ Falling'}
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700/50 bg-slate-800/20">
              <p className="text-[10px] text-slate-500 text-center">
                Data from community scent reports • Updated in real-time
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default TrendingScents;
